import type { OpportunityStageValue, OpportunityView } from "@gioia365/contracts";
import type { Locale } from "@gioia365/i18n";
import type { Tone } from "../design/components/tone";
import { formatEuroCents, formatNumericDate, formatNumericDateTime, localDayOf } from "../i18n/format";
import type { Translate } from "../i18n/locale";

// Le parole delle Opportunità (piano M6, R8, Task 25), le stesse nella pagina, nel riquadro e nella finestra della scheda: la fase, il
// prodotto, il motivo di «Persa», il valore, il prossimo passo, le fasi raggiunte e la fase di adesso. Date e ore in numeri, nel fuso dell'azienda (UX-5); gli importi nel formato della
// lingua della persona.

/** Il tono di una fase, come nel mock-up (`OPP_TONE`): le disco blu, le demo gialle, «Venduto» verde, «Persa» rossa. */
export const STAGE_TONES: Readonly<Record<OpportunityStageValue, Tone>> = {
  disco_booked: "accent",
  disco_held: "accent",
  demo_booked: "warning",
  demo_held: "warning",
  sold: "success",
  lost: "danger",
};

export const isClosedStage = (stage: OpportunityStageValue) => stage === "sold" || stage === "lost";

export function stageLabel(t: Translate, stage: OpportunityStageValue): string {
  return t(`opportunities.stages.${stage}`);
}

/** Il nome del Lead dell'Opportunità: il nome composto dal server, altrimenti il salone. */
export function leadNameOf(opportunity: OpportunityView): string {
  return opportunity.lead.displayName ?? opportunity.lead.salonName ?? "—";
}

/** Il motivo di «Persa», con la nota quando c'è. */
export function lostReasonText(t: Translate, lost: NonNullable<OpportunityView["lost"]>): string {
  const reason = t(`opportunities.lostReasons.${lost.reason}`);
  return lost.note ? t("opportunities.reasonWithNote", { reason, note: lost.note }) : reason;
}

/** Il giorno di chiusura, in numeri; «—» se non c'è. */
function closedDay(timeZone: string, opportunity: OpportunityView): string {
  return opportunity.closedAt ? formatNumericDate(timeZone, opportunity.closedAt) : "—";
}

/** Che cosa ha comprato: Club o Percorso, «Non indicato» per una vendita importata da Close che non lo dice. */
export function productText(t: Translate, product: "club" | "percorso" | null): string {
  return product === null ? t("reports.campaignSales.productUnknown") : t(`opportunities.products.${product}`);
}

/** Il valore di una vendita; «valore non indicato» se Close non lo dice. */
export function saleValueText(t: Translate, locale: Locale, valueCents: number | null): string {
  return valueCents === null ? t("opportunities.valueUnknown") : formatEuroCents(locale, valueCents);
}

/** «Venduto» e «Persa» come prossimo passo: che cosa ha comprato, per quanto e quando; perché e quando. */
function closedText(t: Translate, locale: Locale, timeZone: string, opportunity: OpportunityView): string | null {
  if (opportunity.stage === "sold" && opportunity.sale) {
    return t("opportunities.next.sold", {
      product: productText(t, opportunity.sale.product),
      value: saleValueText(t, locale, opportunity.sale.valueCents),
      date: closedDay(timeZone, opportunity),
    });
  }
  if (opportunity.stage === "lost" && opportunity.lost) return t("opportunities.next.lost", { date: closedDay(timeZone, opportunity), reason: lostReasonText(t, opportunity.lost) });
  return null;
}

/**
 * «Da riprogrammare» dopo un no-show o un annullamento (`nextStep`): dopo un no-show, l'ultimo appuntamento non annullato è proprio
 * quello in cui il Lead non si è presentato, e lo si dice come il mock-up.
 */
function rescheduleText(t: Translate, opportunity: OpportunityView): string {
  const last = opportunity.appointment;
  return last?.status === "done" && last.result === "no_show" ? t("opportunities.noShow") : t("opportunities.reschedule");
}

/** Il prossimo passo di una carta di «Per fase» e di una riga della «Tabella» (`oppNextStep` del mock-up). */
export function nextStepText(t: Translate, locale: Locale, timeZone: string, opportunity: OpportunityView): string {
  if (isClosedStage(opportunity.stage)) return closedText(t, locale, timeZone, opportunity) ?? stageLabel(t, opportunity.stage);
  if (opportunity.nextStep === "reschedule") return rescheduleText(t, opportunity);
  const appointment = opportunity.appointment;
  if (!appointment) return boxStepText(t, locale, timeZone, opportunity);
  return t(`opportunities.next.${opportunity.stage}`, { when: formatNumericDateTime(timeZone, appointment.startsAt) });
}

/**
 * Il prossimo passo del riquadro della scheda (brief del Task 25): «Disco da fare» con l'ora della disco in programma, poi
 * «Disco fatta, demo da prenotare», «Demo da fare», «Demo fatta, da chiudere»; dopo la chiusura come nella pagina.
 */
export function boxStepText(t: Translate, locale: Locale, timeZone: string, opportunity: OpportunityView): string {
  if (isClosedStage(opportunity.stage)) return closedText(t, locale, timeZone, opportunity) ?? stageLabel(t, opportunity.stage);
  if (opportunity.nextStep === "reschedule") return rescheduleText(t, opportunity);
  const step = t(`opportunities.step.${opportunity.stage as Exclude<OpportunityStageValue, "sold" | "lost">}`);
  const appointment = opportunity.appointment;
  const booked = opportunity.stage === "disco_booked" || opportunity.stage === "demo_booked";
  return booked && appointment?.status === "open" ? t("opportunities.step.at", { step, when: formatNumericDateTime(timeZone, appointment.startsAt) }) : step;
}

/**
 * Il mese di adesso nel fuso dell'azienda, per «Vendute a …» e «Perse a …» (mock-up: «Vendute a settembre»): il primo e l'ultimo giorno
 * (`AAAA-MM-GG`, per `period=custom`) e il nome del mese nella lingua della persona («ottobre», «October»).
 */
export function currentMonthOf(locale: Locale, timeZone: string, now: number): { from: string; to: string; name: string } {
  const [year = 1970, month = 1] = localDayOf(timeZone, new Date(now).toISOString()).split("-").map(Number);
  const twoDigits = (value: number) => String(value).padStart(2, "0");
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const name = new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(year, month - 1, 15)));
  return { from: `${year}-${twoDigits(month)}-01`, to: `${year}-${twoDigits(month)}-${twoDigits(lastDay)}`, name };
}

/**
 * Le fasi raggiunte, come nel mock-up: la disco sempre, la demo da «Demo prenotata» (o persa dopo una demo), «Venduto» soltanto
 * venduta; senza un'Opportunità nessuna. Le usano il dettaglio della pagina e la finestra della scheda.
 */
export function reachedSteps(opportunity: OpportunityView | null, demoSeen: boolean): Record<"disco" | "demo" | "sold", boolean> {
  if (!opportunity) return { disco: false, demo: false, sold: false };
  const demoStage = opportunity.stage === "demo_booked" || opportunity.stage === "demo_held";
  return { disco: true, demo: demoStage || opportunity.stage === "sold" || (opportunity.stage === "lost" && demoSeen), sold: opportunity.stage === "sold" };
}

/** Che cosa dire accanto alla fase di adesso: la vendita, il motivo della perdita, il perché da riprogrammare o l'ora dell'appuntamento. */
function stageDetail(t: Translate, locale: Locale, timeZone: string, opportunity: OpportunityView): string | null {
  if (opportunity.stage === "sold") {
    return opportunity.sale
      ? t("opportunities.window.sale", { product: productText(t, opportunity.sale.product), value: saleValueText(t, locale, opportunity.sale.valueCents) })
      : null;
  }
  if (opportunity.stage === "lost") return opportunity.lost ? lostReasonText(t, opportunity.lost) : null;
  if (opportunity.nextStep === "reschedule") return rescheduleText(t, opportunity);
  return opportunity.appointment ? formatNumericDateTime(timeZone, opportunity.appointment.startsAt) : null;
}

/**
 * «Fase di adesso» della finestra «Opportunità di …» (mock-up: `oppText`, la fase e il suo quando): «Demo prenotata · 09/10/2026 15:00»,
 * «Venduto · Percorso · 2.400 €», «Persa · Prezzo»; senza un'Opportunità aperta né chiusa, «Nessuna opportunità aperta».
 */
export function stageNowText(t: Translate, locale: Locale, timeZone: string, opportunity: OpportunityView | null): string {
  if (!opportunity) return t("opportunities.window.none");
  const stage = stageLabel(t, opportunity.stage);
  const detail = stageDetail(t, locale, timeZone, opportunity);
  return detail === null ? stage : t("opportunities.window.stageWith", { stage, detail });
}
